/**
 * Genesys embed detection + Online/Offline derivation (browser side).
 *
 * The app is launched from Genesys in one of two shapes (see the provisioned
 * org config):
 *
 *  - **Interaction Widget** (in-call screen-pop): the URL carries
 *    `gcInteractionId` (plus `gcHostOrigin`, `gcTargetEnv`, `gcLangTag`). An
 *    active interaction ⇒ RBAC **Online**.
 *  - **Custom Client App** (standalone, out-of-conversation): the URL carries
 *    the `gc*` params but NO `gcInteractionId` ⇒ RBAC **Offline**.
 *
 * Outside Genesys (a plain browser, e.g. the local demo) none of the `gc*`
 * params are present ⇒ "not embedded", and the app keeps its existing demo
 * behaviour (the server's header-stub Online/Offline resolver wins).
 *
 * This module is pure URL parsing — no SDK, no network — so it is trivially
 * testable and safe to run on every load.
 */

import type { InteractionState } from '../session.js';

/** Genesys query parameters passed by both integrations. */
export interface GenesysEmbedParams {
  /** Active interaction id; present ONLY for the Interaction Widget (in-call). */
  readonly interactionId?: string;
  /** Origin of the Genesys host page (used by the Client Apps SDK / postMessage). */
  readonly hostOrigin?: string;
  /** Genesys target environment (region/host), e.g. `mypurecloud.ie`. */
  readonly targetEnv?: string;
  /** BCP-47 language tag of the Genesys desktop, e.g. `en-IE`. */
  readonly langTag?: string;
}

/**
 * The resolved embed context.
 *
 *  - `embedded`: true when launched from Genesys (any `gc*` host param present).
 *  - `state`: Online when an interaction is active, otherwise Offline.
 *  - `state` is `undefined` when NOT embedded, signalling callers to fall back
 *    to the server-provided session state (demo behaviour).
 */
export interface EmbedContext {
  readonly embedded: boolean;
  readonly state?: InteractionState;
  readonly params: GenesysEmbedParams;
}

/** Parses the Genesys `gc*` query parameters from a query string. */
export function parseEmbedParams(search: string): GenesysEmbedParams {
  const q = new URLSearchParams(search);
  const nonEmpty = (key: string): string | undefined => {
    const value = q.get(key);
    return value && value.trim() !== '' ? value : undefined;
  };
  return {
    interactionId: nonEmpty('gcInteractionId'),
    hostOrigin: nonEmpty('gcHostOrigin'),
    targetEnv: nonEmpty('gcTargetEnv'),
    langTag: nonEmpty('gcLangTag'),
  };
}

/**
 * Resolves the embed context from a query string.
 *
 * Embedding is detected from `gcHostOrigin` (passed by BOTH integrations); the
 * presence of `gcInteractionId` then distinguishes Online (Interaction Widget)
 * from Offline (standalone Client App). An interaction id without a host origin
 * (unusual, but possible in some hosts) is still treated as embedded+Online.
 */
export function resolveEmbedContext(search: string): EmbedContext {
  const params = parseEmbedParams(search);
  const embedded =
    params.hostOrigin !== undefined ||
    params.targetEnv !== undefined ||
    params.interactionId !== undefined;

  if (!embedded) {
    return { embedded: false, state: undefined, params };
  }
  return {
    embedded: true,
    state: params.interactionId ? 'online' : 'offline',
    params,
  };
}

/** Reads the embed context from the current browser location. */
export function readEmbedContext(
  location: { search: string } = window.location,
): EmbedContext {
  return resolveEmbedContext(location.search);
}
